import { http } from '@/api/http'
import type { Warehouse, WarehouseEmployee, WarehouseHREmployee } from '@/types/api'
import { createCrudService, type RequestOptions } from '../crud'

export type WarehouseInput = Pick<Warehouse, 'branch_id' | 'name' | 'is_active' | 'location' | 'coordinates' | 'employee_ids' | 'hr_employee_ids'>

const crud = createCrudService<Warehouse, WarehouseInput>('warehouses')
const employeesPath = (id: string) => `/warehouses/${encodeURIComponent(id)}/employees`

async function listEmployees(id: string, options?: RequestOptions) {
  const { data } = await http.get<WarehouseEmployee[]>(employeesPath(id), options)
  return data ?? []
}

async function replaceEmployees(id: string, profileIds: string[]) {
  const { data } = await http.put<WarehouseEmployee[]>(employeesPath(id), { profile_ids: profileIds })
  return data ?? []
}

/**
 * Warehouses keep independent login access (`employee_ids`) and HR personnel
 * (`hr_employee_ids`) assignments, saved through their dedicated endpoints.
 */
export const warehousesService = {
  ...crud,
  listEmployees,
  replaceEmployees,
  async listHREmployees(id: string, options?: RequestOptions) {
    const { data } = await http.get<WarehouseHREmployee[]>(`/warehouses/${encodeURIComponent(id)}/hr-employees`, options)
    return data ?? []
  },
  async replaceHREmployees(id: string, employeeIds: string[]) {
    const { data } = await http.put<WarehouseHREmployee[]>(`/warehouses/${encodeURIComponent(id)}/hr-employees`, { employee_ids: employeeIds })
    return data ?? []
  },

  async get(id: string, options?: RequestOptions) {
    const [warehouse, employees, personnel] = await Promise.all([crud.get(id, options), listEmployees(id, options), warehousesService.listHREmployees(id, options)])
    return { ...warehouse, employee_ids: employees.map((employee) => employee.profile_id), hr_employee_ids: personnel.map((employee) => employee.employee_id) }
  },

  async create({ employee_ids, hr_employee_ids, ...body }: WarehouseInput) {
    const warehouse = await crud.create(body)
    if (employee_ids?.length) await replaceEmployees(warehouse.id, employee_ids)
    if (hr_employee_ids?.length) await warehousesService.replaceHREmployees(warehouse.id, hr_employee_ids)
    return { ...warehouse, employee_ids, hr_employee_ids }
  },

  async update(id: string, { employee_ids, hr_employee_ids, ...body }: Partial<WarehouseInput>) {
    const warehouse = Object.keys(body).length > 0 ? await crud.update(id, body) : await crud.get(id)
    if (employee_ids) await replaceEmployees(id, employee_ids)
    if (hr_employee_ids) await warehousesService.replaceHREmployees(id, hr_employee_ids)
    return { ...warehouse, employee_ids, hr_employee_ids }
  },
}
